import React from 'react';
import { Box, Typography, Grid, Card, CardContent, CircularProgress } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Thermostat as ThermostatIcon,
  Air as AirIcon,
  Opacity as OpacityIcon,
} from '@mui/icons-material';
import { useMonthlyWeatherStatistics, useWeatherStatistics } from '../../api/weatherHistory';
import { useUnitSystem } from '../../hooks/useUnitSystem';
import { formatSpeed, formatTemperature, toDisplayTemperature } from '../../utils/units';
import dayjs from 'dayjs';

interface SeasonalTrendsViewProps {
  projectId: string;
  weatherDate: dayjs.Dayjs;
}

const SeasonalTrendsView: React.FC<SeasonalTrendsViewProps> = ({ projectId, weatherDate }) => {
  const { data: monthlyStats, isLoading: isLoadingMonthly } = useMonthlyWeatherStatistics(projectId);
  const { data: stats, isLoading: isLoadingStats } = useWeatherStatistics(projectId);
  const isLoading = isLoadingMonthly || isLoadingStats;
  const unitSystem = useUnitSystem();

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const chartData = monthlyStats.map((stat) => ({
    month: monthNames[stat.month - 1],
    avgTemp: toDisplayTemperature(stat.avg_temperature, unitSystem),
    maxTemp: toDisplayTemperature(stat.avg_daily_max_temperature, unitSystem),
    humidity: stat.avg_humidity,
  }));

  // Use the weather date's month for highlighting
  const currentMonth = weatherDate.month() + 1; // dayjs months are 0-indexed
  const currentMonthStats = monthlyStats.find(s => s.month === currentMonth);
  const meanMaxTemp = currentMonthStats?.avg_daily_max_temperature || 0;
  const meanWindSpeed = currentMonthStats?.avg_wind_speed || 0;
  const dominantWindDirection = currentMonthStats?.dominant_wind_direction || 'N/A';
  const currentMonthName = monthNames[currentMonth - 1];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress sx={{ color: '#ff6b35' }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={4}>
          <Card sx={{ bgcolor: 'rgba(255, 107, 53, 0.1)', border: '1px solid rgba(255, 107, 53, 0.3)', height: '100%' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ThermostatIcon sx={{ color: '#ff6b35' }} />
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', textTransform: 'uppercase' }}>
                  Max Temp
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ color: '#fff', fontWeight: 600 }}>
                {formatTemperature(meanMaxTemp, unitSystem)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                Average daily maximum for {currentMonthName}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={4}>
          <Card sx={{ bgcolor: 'rgba(100, 181, 246, 0.1)', border: '1px solid rgba(100, 181, 246, 0.3)', height: '100%' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AirIcon sx={{ color: '#64b5f6' }} />
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', textTransform: 'uppercase' }}>
                  Dominant Wind
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ color: '#fff', fontWeight: 600 }}>
                {dominantWindDirection}
              </Typography>
              <Typography variant="h4" sx={{ color: '#fff', fontWeight: 400 }}>
                {meanWindSpeed ? formatSpeed(meanWindSpeed, unitSystem) : 'N/A'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                Average for {currentMonthName}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={4}>
          <Card sx={{ bgcolor: 'rgba(255, 152, 0, 0.1)', border: '1px solid rgba(255, 152, 0, 0.3)', height: '100%' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <OpacityIcon sx={{ color: '#ff9800' }} />
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', textTransform: 'uppercase' }}>
                  Drought Factor
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ color: '#fff', fontWeight: 600 }}>
                {currentMonthStats?.avg_drought_factor != null
                  ? currentMonthStats.avg_drought_factor.toFixed(1)
                  : stats?.drought_factor?.mean != null
                    ? stats.drought_factor.mean.toFixed(1)
                    : 'N/A'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                Average for {currentMonthName}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Temperature & Humidity Profile Chart */}
      <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
            Temperature & Humidity Profile
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: '#ffa726', borderRadius: '50%' }} />
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Avg Temp</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: '#ff6b35', borderRadius: '50%' }} />
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Max Temp</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: '#64b5f6', borderRadius: '50%' }} />
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Humidity</Typography>
            </Box>
          </Box>
        </Box>

        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
            <XAxis
              dataKey="month"
              stroke="rgba(255, 255, 255, 0.5)"
              tick={{ fill: 'rgba(255, 255, 255, 0.7)' }}
            />
            <YAxis
              yAxisId="temp"
              stroke="rgba(255, 255, 255, 0.5)"
              tick={{ fill: 'rgba(255, 255, 255, 0.7)' }}
              label={{ value: unitSystem === 'imperial' ? '°F' : '°C', angle: -90, position: 'insideLeft', fill: 'rgba(255, 255, 255, 0.7)' }}
            />
            <YAxis
              yAxisId="humidity"
              orientation="right"
              stroke="rgba(255, 255, 255, 0.5)"
              tick={{ fill: 'rgba(255, 255, 255, 0.7)' }}
              label={{ value: '%', angle: 90, position: 'insideRight', fill: 'rgba(255, 255, 255, 0.7)' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 8,
              }}
              labelStyle={{ color: '#fff' }}
            />
            <Line
              yAxisId="temp"
              type="monotone"
              dataKey="avgTemp"
              stroke="#ffa726"
              strokeWidth={2}
              dot={{ fill: '#ffa726', r: 4 }}
            />
            <Line
              yAxisId="temp"
              type="monotone"
              dataKey="maxTemp"
              stroke="#ff6b35"
              strokeWidth={2}
              dot={{ fill: '#ff6b35', r: 4 }}
            />
            <Line
              yAxisId="humidity"
              type="monotone"
              dataKey="humidity"
              stroke="#64b5f6"
              strokeWidth={2}
              dot={{ fill: '#64b5f6', r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default SeasonalTrendsView;
